import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconAlertTriangle,
  IconBook2,
  IconCheck,
  IconClock,
  IconPlayerPlay,
  IconX,
} from '@tabler/icons-react';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { Modal } from '../../components/common/Modal';
import { toast } from '../../components/common/Toaster';
import apiService, { ExamItem, ExamPaper } from '../../services/ApiService';
import {
  getMName,
  getPracticalCenter,
  getUserName,
  setPracticalCenter,
  setUserSession,
} from '../../services/authSession';
import { useCameraContext } from '../../contexts/CameraContext';
import { ExamPortalHeader } from '../examportal/components/ExamPortalHeader';
import { DropDown } from '../../components/common/DropDown';
import { DashboardExamTimer } from './components/DashboardExamTimer';
import { useAntiCheat } from '../../hooks/useAntiCheat';
import { clearAuthAndRedirectToLogin } from '../../services/authNavigation';
import Footer from '../../components/layout/Footer';
import classes from './css/ExamDashboardPage.module.css';

function flattenPapers(exams: ExamItem[]): Array<{ exam: ExamItem; paper: ExamPaper }> {
  return exams.flatMap((exam) => (exam.papers ?? []).map((paper) => ({ exam, paper })));
}

type PendingStart = { exam: ExamItem; paper: ExamPaper };

export default function ExamDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [papers, setPapers] = useState<Array<{ exam: ExamItem; paper: ExamPaper }>>([]);
  const [name, setName] = useState('');
  const [mname, setMName] = useState('');
  const [pCenter, setPCenter] = useState('');
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [pendingStart, setPendingStart] = useState<PendingStart | null>(null);
  const [startingRegularExam, setStartingRegularExam] = useState(false);

  const [practicalModalOpen, setPracticalModalOpen] = useState(false);
  const [availableCenters, setAvailableCenters] = useState<string[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<string>('');
  const [savingCenter, setSavingCenter] = useState(false);
  /** Paper IDs whose "Ends in" timer expired during this session */
  const [expiredEndPaperIds, setExpiredEndPaperIds] = useState<Set<number>>(new Set());

  const { isActive } = useCameraContext();
  useAntiCheat();

  const closeStartModal = useCallback(() => {
    setStartModalOpen(false);
    setPendingStart(null);
    setConsentChecked(false);
  }, []);

  const navigateToRegularExam = useCallback(
    (exam: ExamItem, paper: ExamPaper) => {
      const examTypeUpper = (exam.examType ?? '').toUpperCase();
      if (examTypeUpper === 'REGULAR') {
        const remainingEnd = Number(paper.remainingTimeToEnd ?? exam.remainingTimeToEnd ?? 0);
        if (remainingEnd > 0) {
          const absoluteEnd = new Date(Date.now() + remainingEnd * 1000).toISOString();
          // Update session with new end time using the utility function
          setUserSession(
            undefined,
            undefined,
            undefined,
            undefined,
            new Date().toISOString(),
            absoluteEnd
          );
        }

        const sid =
          paper.sessionId != null
            ? `&sessionId=${encodeURIComponent(String(paper.sessionId))}`
            : '';
        navigate(`/exam?paperId=${paper.paperId}&mode=regular${sid}`);
        return;
      }
      navigate('/exam');
    },
    [navigate]
  );

  const handleModalStartExam = useCallback(async () => {
    if (!consentChecked || !pendingStart || startingRegularExam) return;
    const { exam, paper } = pendingStart;
    const examTypeUpper = (exam.examType ?? '').toUpperCase();

    if (examTypeUpper !== 'REGULAR') {
      closeStartModal();
      navigateToRegularExam(exam, paper);
      return;
    }

    try {
      setStartingRegularExam(true);

      const isStartedStatus = (status: string | undefined) =>
        (status ?? '').toUpperCase() === 'STARTED';

      // Continue exam: use existing session data directly (no refresh needed)
      if (isStartedStatus(paper.sessionStatus)) {
        const sid = paper.sessionId != null ? Number(paper.sessionId) : null;

        if (sid == null || !Number.isFinite(sid)) {
          // Session ID not in list — try refreshing once
          try {
            const exams = await apiService.getAvailableExams();
            const rows = flattenPapers(exams);
            setPapers(rows);
            const refreshed = rows.find(
              (row) => row.paper.paperId === paper.paperId && row.exam.examId === exam.examId
            );
            const rSid =
              refreshed?.paper?.sessionId != null ? Number(refreshed.paper.sessionId) : null;
            if (rSid == null || !Number.isFinite(rSid)) {
              toast.error('Could not find exam session. Please refresh and try again.');
              return;
            }
            // Set end time from refreshed data
            const remEnd = Number(
              refreshed?.paper?.remainingTimeToEnd ?? refreshed?.exam?.remainingTimeToEnd ?? 0
            );
            if (remEnd > 0) {
              const absoluteEnd = new Date(Date.now() + remEnd * 1000).toISOString();
              setUserSession(undefined, undefined, undefined, undefined, undefined, absoluteEnd);
            }
            closeStartModal();
            navigate(
              `/exam?paperId=${paper.paperId}&mode=regular&sessionId=${encodeURIComponent(String(rSid))}`
            );
            return;
          } catch {
            toast.error('Could not load exam session. Please refresh and try again.');
            return;
          }
        }

        // Set end time if available
        const remEnd = Number(paper.remainingTimeToEnd ?? exam.remainingTimeToEnd ?? 0);
        if (remEnd > 0) {
          const absoluteEnd = new Date(Date.now() + remEnd * 1000).toISOString();
          setUserSession(undefined, undefined, undefined, undefined, undefined, absoluteEnd);
        }

        closeStartModal();
        navigate(
          `/exam?paperId=${paper.paperId}&mode=regular&sessionId=${encodeURIComponent(String(sid))}`
        );
        return;
      }

      const fromStart = await apiService.startRegularExam(paper.paperId);
      let sessionId: number | null = null;

      if (fromStart != null && Number.isFinite(fromStart)) {
        sessionId = fromStart;
      }

      // If start API didn't return a session ID, re-fetch the exam list
      if (sessionId == null) {
        const refreshedExams = await apiService.getAvailableExams();
        const refreshedRows = flattenPapers(refreshedExams);
        setPapers(refreshedRows);
        const match = refreshedRows.find(
          (row) => row.paper.paperId === paper.paperId && row.exam.examId === exam.examId
        );
        const fetchedSid = match?.paper?.sessionId != null ? Number(match.paper.sessionId) : null;
        if (fetchedSid != null && Number.isFinite(fetchedSid)) {
          sessionId = fetchedSid;
        }
        // Also update end time from refreshed data if available
        const remEndRefreshed = Number(
          match?.paper?.remainingTimeToEnd ?? match?.exam?.remainingTimeToEnd ?? 0
        );
        if (remEndRefreshed > 0) {
          const absoluteEnd = new Date(Date.now() + remEndRefreshed * 1000).toISOString();
          setUserSession(undefined, undefined, undefined, undefined, undefined, absoluteEnd);
        }
      }

      if (sessionId == null) {
        toast.error('Could not start the exam. Please try again.');
        return;
      }

      // Set end time from paper data if not already set via refreshed exams
      const remEnd = Number(paper.remainingTimeToEnd ?? exam.remainingTimeToEnd ?? 0);
      if (remEnd > 0) {
        const absoluteEnd = new Date(Date.now() + remEnd * 1000).toISOString();
        setUserSession(undefined, undefined, undefined, undefined, undefined, absoluteEnd);
      }

      closeStartModal();
      navigate(
        `/exam?paperId=${paper.paperId}&mode=regular&sessionId=${encodeURIComponent(String(sessionId))}`
      );
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearAuthAndRedirectToLogin('session_invalid');
        return;
      }
      const msg = err?.response?.data?.msg || err?.message || 'Failed to start exam.';
      toast.error(msg);
    } finally {
      setStartingRegularExam(false);
    }
  }, [
    consentChecked,
    pendingStart,
    startingRegularExam,
    closeStartModal,
    navigateToRegularExam,
    navigate,
  ]);

  const handleSaveCenter = useCallback(async () => {
    if (!selectedCenter || savingCenter) return;
    try {
      setSavingCenter(true);
      await apiService.savePracticalCenter(selectedCenter);
      setPracticalCenter(selectedCenter);
      setPCenter(selectedCenter);
      setPracticalModalOpen(false);
      toast.success('Practical exam center saved successfully.');
    } catch (err: any) {
      toast.error(err?.response?.data?.msg || err?.message || 'Failed to save practical center.');
    } finally {
      setSavingCenter(false);
    }
  }, [selectedCenter, savingCenter]);

  useEffect(() => {
    const center = getPracticalCenter();
    if (!center) {
      apiService
        .getPracticalCenters()
        .then((data) => {
          setAvailableCenters(data);
          setPracticalModalOpen(true);
        })
        .catch((err) => {
          console.error('Failed to fetch practical centers:', err);
        });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const exams = await apiService.getAvailableExams();
        if (!mounted) return;
        setName(getUserName());
        setMName(getMName());
        setPCenter(getPracticalCenter());
        setPapers(flattenPapers(exams));
      } catch (err: any) {
        if (!mounted) return;
        if (err?.response?.status === 401) {
          clearAuthAndRedirectToLogin('session_invalid');
          return;
        }
        const msg = err?.response?.data?.msg || err?.message || 'Failed to load exams.';
        toast.error(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const greetingName = useMemo(() => name || 'Student', [name]);

  return (
    <div className={classes.page}>
      <ExamPortalHeader isDashboard />

      <main className={classes.main}>
        <h1 className={classes.title}>Welcome, {greetingName}!</h1>

        <Card className={classes.verifyCard} shadow="sm" radius="md">
          <IconAlertTriangle size={22} className={classes.verifyIcon} aria-hidden />
          <div>
            <h3 className={classes.verifyTitle}>Verify Your Information</h3>
            <p className={classes.verifyText}>
              Please verify your name in both English: <strong>{name}</strong> and Devnagari:{' '}
              <strong>{mname || '--'}</strong>.
            </p>
            <p className={classes.verifyText}>
              Practical Exam Center: <strong>{pCenter || 'Not Selected'}</strong>
            </p>
            <p className={classes.verifyText}>
              If there is any spelling mistake or information mismatch, please contact in the
              office.
            </p>
            <p className={classes.verifyText}>As it will be added as it is in the Certificate.</p>
          </div>
        </Card>
        <p className={classes.subTitle}>View your available exams here</p>
        {loading ? (
          <Card className={classes.loadingCard} shadow="sm" radius="md">
            Loading available exams...
          </Card>
        ) : (
          <div className={classes.cardsList}>
            {papers.map(({ exam, paper }) => {
              const examTypeUpper = (exam.examType ?? '').toUpperCase();
              const isSample = examTypeUpper === 'SAMPLE';
              const status = (paper.sessionStatus ?? '').toUpperCase();
              const examStatus = (exam.status ?? '').toUpperCase();
              const isCompleted = examStatus === 'COMPLETED';
              const isStarted = status === 'STARTED';
              const isNotStarted = status === 'NOT_STARTED';
              const isSubmitted = status === 'SUBMITTED';
              const isAutoSubmit = status === 'AUTO_SUBMIT' || status === 'AUTO_SUBMITTED';
              const remainingStart = Number(
                paper.remainingTimeToStart ?? exam.remainingTimeToStart ?? 0
              );
              const remainingEnd = Number(paper.remainingTimeToEnd ?? exam.remainingTimeToEnd ?? 0);
              const isEndExpiredLocally = expiredEndPaperIds.has(paper.paperId);
              const isExamTimeExpired = isCompleted || isEndExpiredLocally;
              const isAvailableState = isStarted || isNotStarted;
              const isGated = !isSample && remainingStart > 0;
              const isEnabled = isAvailableState && !isGated && !isExamTimeExpired;

              const isPracticalCenterFilled = Boolean(getPracticalCenter());

              const startExam = () => {
                if (!isEnabled) return;
                if (!isPracticalCenterFilled && !isSample) {
                  toast.error('Please select your practical exam center to continue.');
                  setPracticalModalOpen(true);
                  return;
                }
                if (!isActive && !isSample) {
                  toast.error('Camera must be enabled to start the exam.');
                  return;
                }
                if (isSample) {
                  navigate(`/exam?paperId=${paper.paperId}&mode=sample`);
                  return;
                }
                setPendingStart({ exam, paper });
                setConsentChecked(false);
                setStartModalOpen(true);
              };

              let badgeClass = classes.notStarted;
              if (isStarted) badgeClass = classes.started;
              if (isSubmitted) badgeClass = classes.submitted;
              if (isAutoSubmit) badgeClass = classes.autoSubmit;

              let badgeLabel = status;
              if (isAutoSubmit) badgeLabel = 'AUTO SUBMITTED';
              return (
                <Card
                  key={`${exam.examId}-${paper.paperId}`}
                  className={classes.examCard}
                  shadow="sm"
                  radius="md"
                >
                  <div className={classes.cardHeader}>
                    <div className={classes.examIconWrap}>
                      <IconBook2 size={20} />
                    </div>
                    <div className={classes.badges}>
                      <span className={classes.paperTypeBadge}>{paper.paperType}</span>
                      <span className={`${classes.sessionBadge} ${badgeClass}`}>{badgeLabel}</span>
                    </div>
                  </div>

                  <h2 className={classes.examName}>{paper.name}</h2>
                  <p className={classes.examType}>{exam.name}</p>

                  <div className={classes.stats}>
                    <div className={classes.statItem}>
                      <IconClock size={14} />
                      <span>{exam.durationMinutes ?? '--'} Minutes</span>
                    </div>
                    <div className={classes.statItem}>
                      <span>{paper.totalMarks} Marks</span>
                    </div>
                  </div>

                  {(isSubmitted || isAutoSubmit) && paper.submittedTime && (
                    <div className={classes.submittedTimeInfo}>
                      <IconCheck size={16} className={classes.submittedIcon} />
                      <span>
                        {isAutoSubmit ? 'Auto Submitted' : 'Submitted'} on: {paper.submittedTime}
                      </span>
                    </div>
                  )}

                  {/* Exam Time Expired banner — COMPLETED status or locally expired "Ends in" timer */}
                  {!isSample && !isSubmitted && !isAutoSubmit && isExamTimeExpired && (
                    <div className={classes.examExpiredBanner}>
                      <IconAlertTriangle size={16} />
                      <span>
                        Exam Time Expired
                        {exam.endTime ? ` at ${exam.endTime}` : ''}
                      </span>
                    </div>
                  )}

                  {!isSample && !isSubmitted && !isAutoSubmit && !isExamTimeExpired && (
                    <>
                      {remainingStart > 0 ? (
                        <DashboardExamTimer
                          remainingSeconds={remainingStart}
                          label="Starts in"
                          onFinish={() => {
                            setPapers((prev) =>
                              prev.map((item) =>
                                item.paper.paperId === paper.paperId
                                  ? { ...item, paper: { ...item.paper, remainingTimeToStart: 0 } }
                                  : item
                              )
                            );
                          }}
                        />
                      ) : remainingEnd > 0 ? (
                        <DashboardExamTimer
                          remainingSeconds={remainingEnd}
                          label="Ends in"
                          onFinish={() => {
                            setExpiredEndPaperIds((prev) => new Set(prev).add(paper.paperId));
                          }}
                        />
                      ) : null}
                    </>
                  )}

                  <Button
                    className={
                      isSample
                        ? classes.practiceBtn
                        : isAvailableState && !isExamTimeExpired
                          ? classes.startBtn
                          : classes.submittedBtn
                    }
                    onClick={startExam}
                    disabled={(!isEnabled && !isSample) || isExamTimeExpired}
                  >
                    {(!isEnabled && !isSample) || isExamTimeExpired ? (
                      <IconCheck size={16} />
                    ) : (
                      <IconPlayerPlay size={16} />
                    )}
                    {isSample
                      ? 'Practice Now'
                      : isExamTimeExpired
                        ? 'Exam Expired'
                        : isStarted
                          ? 'Continue Exam'
                          : isAvailableState
                            ? 'Start Exam'
                            : isAutoSubmit
                              ? 'Auto Submitted'
                              : 'Exam Submitted'}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Modal
        opened={startModalOpen}
        onClose={closeStartModal}
        title={null}
        size="min(480px, 92vw)"
        className={classes.examStartModal}
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
          content: { overflow: 'hidden' },
        }}
      >
        {pendingStart && (
          <div className={classes.examStartModalInner}>
            <div className={classes.examModalHeader}>
              <button
                type="button"
                className={classes.examModalClose}
                onClick={closeStartModal}
                aria-label="Close"
              >
                <IconX size={20} stroke={2} />
              </button>
              <h2 className={classes.examModalTitle}>{pendingStart.exam.name}</h2>
              <p className={classes.examModalSubtitle}>{pendingStart.paper.name}</p>
            </div>

            <div className={classes.examModalBody}>
              <div className={classes.examModalStatus}>
                <span className={classes.examModalStatusIcon} aria-hidden>
                  <IconCheck size={18} stroke={3} />
                </span>
                <span className={classes.examModalStatusText}>
                  Exam is now open! Click Start below.
                </span>
              </div>

              <div className={classes.examModalStats}>
                <div className={classes.examModalStat}>
                  <span className={classes.examModalStatValue}>
                    {pendingStart.exam.durationMinutes ?? '—'}
                  </span>
                  <span className={classes.examModalStatLabel}>Minutes</span>
                </div>
                <div className={classes.examModalStat}>
                  <span className={classes.examModalStatValue}>
                    {pendingStart.paper.totalMarks}
                  </span>
                  <span className={classes.examModalStatLabel}>Marks</span>
                </div>
              </div>

              <div className={classes.examModalInstructions}>
                <div className={classes.examModalInstructionsTitle}>
                  <IconAlertTriangle size={18} className={classes.examModalWarnIcon} aria-hidden />
                  महत्वपूर्ण निर्देश / Important Instructions
                </div>
                <div className={classes.instructionLangGroup}>
                  <p className={classes.instructionLangHeader}>हिन्दी (Hindi)</p>
                  <ol className={classes.examModalInstructionsList}>
                    <li>
                      परीक्षा के दौरान टैब/विंडो न बदलें और प्रश्नों को सिलेक्ट करने का प्रयास न
                      करें। ऐसा करने पर 5 चेतावनियों के बाद पत्र अपने आप सबमिट हो जाएगी।
                    </li>
                    <li>आपके उत्तर स्वाभाविक रूप से 'सेव्ह' हो जाते हैं।</li>
                    <li>आप प्रश्नों के बीच खुलकर नेविगेट कर सकते हैं।</li>
                    <li>समय समाप्त होने पर परीक्षा अपने आप सबमिट हो जाएगी।</li>
                    <li>परीक्षा के दौरान कैमरा अनिवार्य रूप से चालू रखना आवशयक है।</li>
                    <li>परीक्षा के दौरान स्थिर इंटरनेट कनेक्शन का उपयोग करें।</li>
                    <li>
                      जब इंटरनेट लिंक काम ना करे या इंटरनेट डिस्कनेक्ट हो जाए तो पेज को पुनः रिलोड /
                      रिफ्रेश न करें। आपको सूचना एवं चेतावनी मिलेगी, कृपया उसे नजरअंदाज न करें।
                    </li>
                    <li>
                      यदि आपको इंटरनेट कनेक्शन बदलना हो, तो अपनी कक्षा के शिक्षक से संपर्क करें।
                    </li>
                    <li>
                      आप ऑफलाइन मोड में, यानी जब इंटरनेट डिस्कनेक्ट हो, तब भी उत्तर दे सकते हैं,
                      इससे आपकी परीक्षा बाधित नहीं होगी। सबमिशन या ऑटो सबमिशन के समय मात्र इंटरनेट
                      चालू रहना चाहिए, अन्यथा केवल वही प्रश्न जमा होंगे जो पूर्व समय ऑनलाइन मोड के
                      दौरान उत्तर दिए गए हैं।
                    </li>
                    <li>किसी भी समस्या की स्थिति में कृपया एडमिन से संपर्क करें।</li>
                  </ol>
                </div>
                <div className={classes.instructionLangGroup}>
                  <p className={classes.instructionLangHeader}>English</p>
                  <ol className={classes.examModalInstructionsList}>
                    <li>
                      Do not switch tabs or windows or select the questions during the exam
                      otherwise it will be auto submit after 5 warnings.
                    </li>
                    <li>Your answers are saved automatically.</li>
                    <li>You can navigate freely between questions.</li>
                    <li>Exam will auto-submit when time expires.</li>
                    <li>It is mandatory to keep the camera turned on during the examination.</li>
                    <li>Use stable internet connection during exam.</li>
                    <li>
                      Don’t refresh the page when the internet is disconnected, you will get a
                      warning. Please do not ignore that.
                    </li>
                    <li>If you want to switch internet, contact invigilator.</li>
                    <li>
                      You can still answer in offline mode, i.e., when the internet is disconnected.
                      Your exam will continue uninterrupted. During submission or auto-submission,
                      the internet should be available. Otherwise, whichever questions were answered
                      during online mode will be submitted.
                    </li>
                    <li>Incase of any issues please contact admin.</li>
                  </ol>
                </div>
              </div>

              <label className={classes.examModalConsent}>
                <input
                  type="checkbox"
                  className={classes.examModalCheckbox}
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <div className={classes.consentText}>
                  <p style={{ marginTop: '8px', fontWeight: 500 }}>
                    मैं यहाँ पुष्टि करता/करती हूँ कि मैंने ऊपर दिए गए सभी निर्देशों को पढ़ लिया है
                    और समझ लिया है।
                  </p>
                  <p>
                    I hereby confirm that I have read and understood all the above instructions. I
                    agree to follow all the rules and regulations during the examination. I
                    understand that any violation may lead to disqualification.
                  </p>
                </div>
              </label>

              <Button
                type="button"
                className={classes.examModalStartBtn}
                disabled={!consentChecked || !isActive || !getPracticalCenter()}
                onClick={handleModalStartExam}
              >
                {startingRegularExam ? 'Starting…' : 'Start Exam'}
              </Button>

              <button type="button" className={classes.examModalGoBack} onClick={closeStartModal}>
                Go Back
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        opened={practicalModalOpen}
        onClose={() => {}} // Mandatory selection
        title={<span className={classes.pCenterTitle}>Select Practical Exam Center</span>}
        size="min(440px, 90vw)"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <div className={classes.pCenterBody}>
          <p className={classes.pCenterText}>
            Please select your preferred center for the practical examination. This selection is
            mandatory to proceed.
          </p>
          <DropDown
            value={selectedCenter}
            options={availableCenters.map((c) => ({ value: c, label: c }))}
            onChange={(val) => setSelectedCenter(val || '')}
          />
          <Button
            className={classes.pCenterSaveBtn}
            onClick={handleSaveCenter}
            disabled={!selectedCenter || savingCenter}
          >
            {savingCenter ? 'Saving...' : 'Save Selection'}
          </Button>
        </div>
      </Modal>
      <Footer />
    </div>
  );
}
